import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getCandidates, applyForJob, getJobApplications, getJobById, getJobApplicationSources, updateJobApplicationStatus, addJobComment } from "../api";
import { useAuth } from "../AuthContext";
import { resourceStatusOptions } from '../services/dropdownOptions';
import PopupMessage from "./PopupMessage";

const JobDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialJob = location.state?.job || null;
  const initialCandidateId = location.state?.candidateId || initialJob?.candidate_id || null;
  const initialJobId = location.state?.jobId || initialJob?.job_id || initialJob?.id || null;

  const [job, setJob] = useState(initialJob);
  const [jobId, setJobId] = useState(initialJobId);
  const [candidateId, setCandidateId] = useState(initialCandidateId);
  const [jobApplications, setJobApplications] = useState([]);
  const [applicationSources, setApplicationSources] = useState([]);
  // source add/update handled via AddJobPage popup; display only here
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Edit popup state
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [editFormData, setEditFormData] = useState({
    status: "",
    job_comment: ""
  });
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showSourceDetails, setShowSourceDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [showAddCommentPopup, setShowAddCommentPopup] = useState(false);
  const [newCommentStatus, setNewCommentStatus] = useState('applied');
  const [newComment, setNewComment] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [showAddCandidatePopup, setShowAddCandidatePopup] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [applyingCandidate, setApplyingCandidate] = useState(false);
  const targetCandidateId = candidateId || user?.candidate_id;
  const showAddCommentButton = Boolean(candidateId || user?.candidate_id);
  const isAppliedCandidateRoute = location.pathname === '/applied_candidate';

  useEffect(() => {
    if (!jobId) {
      setError("No job ID provided");
      setShowErrorPopup(true);
      return;
    }

    // Clear previous applications when candidate/job changes
    setJobApplications([]);
    window.scrollTo(0, 0);

    const fetchJobApplications = async () => {
      try {
        setLoading(true);
        // Get applications for this job and the selected candidate only
        const response = await getJobApplications(jobId, candidateId);
        if (response.success) {
          setJobApplications(response.data || []);
        } else {
          setError(response.message || "Failed to load job applications");
          setShowErrorPopup(true);
        }
      } catch (err) {
        console.error("Error fetching job applications:", err);
        setError("Network error while loading job applications");
        setShowErrorPopup(true);
      } finally {
        setLoading(false);
      }
    };

    fetchJobApplications();
  }, [jobId, candidateId]);

  useEffect(() => {
    if (job || !jobId) {
      return;
    }

    const fetchJobDetails = async () => {
      try {
        const response = await getJobById(jobId);
        if (response.success) {
          setJob(response.data);
        } else {
          setError(response.message || "Failed to load job details");
          setShowErrorPopup(true);
        }
      } catch (err) {
        console.error("Error fetching job details:", err);
        setError("Network error while loading job details");
        setShowErrorPopup(true);
      }
    };

    fetchJobDetails();
  }, [job, jobId]);

  useEffect(() => {
    if (!jobId) {
      return;
    }

    const fetchApplicationSources = async () => {
      try {
        const response = await getJobApplicationSources(jobId);
        if (response.success) {
          setApplicationSources(response.data || []);
        } else {
          setApplicationSources([]);
          console.error('Failed to load application sources:', response.message);
        }
      } catch (err) {
        console.error('Error fetching application sources:', err);
        setApplicationSources([]);
      }
    };

    fetchApplicationSources();
  }, [jobId]);

  useEffect(() => {
    if (!showAddCandidatePopup || candidates.length > 0) {
      return;
    }

    const fetchCandidates = async () => {
      try {
        setCandidatesLoading(true);
        const response = await getCandidates();
        if (response.success) {
          setCandidates(response.data || []);
        } else {
          setError(response.message || 'Failed to load candidates');
          setShowErrorPopup(true);
        }
      } catch (err) {
        console.error('Error fetching candidates:', err);
        setError('Network error while loading candidates');
        setShowErrorPopup(true);
      } finally {
        setCandidatesLoading(false);
      }
    };

    fetchCandidates();
  }, [showAddCandidatePopup, candidates.length]);

  const handleEditApplication = (application) => {
    setEditingApplication(application);
    setEditFormData({
      status: application.status || "",
      job_comment: application.job_comment || ""
    });
    setShowEditPopup(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenAddComment = () => {
    setNewCommentStatus('applied');
    setNewComment("");
    setShowAddCommentPopup(true);
  };

  const handleCommentStatusChange = (e) => {
    setNewCommentStatus(e.target.value);
  };

  const handleAddCommentChange = (e) => {
    setNewComment(e.target.value);
  };

  const filteredCandidates = useMemo(() => {
    const search = candidateSearch.trim().toLowerCase();
    if (!search) return candidates;

    return candidates.filter(candidate => {
      const candidateIdString = candidate.CandidateID ? String(candidate.CandidateID) : '';
      const fullName = candidate.FullName ? candidate.FullName.toLowerCase() : '';
      return candidateIdString.includes(search) || fullName.includes(search);
    });
  }, [candidates, candidateSearch]);

  const closeAddCandidatePopup = () => {
    setShowAddCandidatePopup(false);
    setCandidateSearch("");
    setSelectedCandidateId(null);
  };

  const handleAddCandidateSubmit = async () => {
    if (!selectedCandidateId) {
      setError('Please select a candidate from the list');
      setShowErrorPopup(true);
      return;
    }

    if (!jobId) {
      setError('Job is not loaded yet');
      setShowErrorPopup(true);
      return;
    }

    setApplyingCandidate(true);
    try {
      const response = await applyForJob(selectedCandidateId, jobId);
      if (response.success) {
        setSuccessMessage('Candidate applied to this job successfully.');
        setShowSuccessPopup(true);
        closeAddCandidatePopup();

        const refreshResponse = await getJobApplications(jobId, candidateId);
        if (refreshResponse.success) {
          setJobApplications(refreshResponse.data || []);
        }
      } else {
        setError(response.message || 'Failed to apply candidate');
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('Error applying candidate:', err);
      setError('Network error while applying candidate');
      setShowErrorPopup(true);
    } finally {
      setApplyingCandidate(false);
    }
  };

  // source add/update handled from AddJobPage; removed editing handlers here

  const handleAddCommentSubmit = async (e) => {
    e.preventDefault();

    if (!targetCandidateId) {
      setError("You must be logged in as a candidate to add comments.");
      setShowErrorPopup(true);
      return;
    }

    try {
      setCommentSubmitting(true);
      const response = await addJobComment(
        targetCandidateId,
        jobId,
        newComment.trim() || null,
        newCommentStatus || 'applied'
      );

      if (response.success) {
        setSuccessMessage("Job comment added successfully.");
        setShowSuccessPopup(true);
        setShowAddCommentPopup(false);
        setNewCommentStatus('applied');
        setNewComment("");

        const refreshResponse = await getJobApplications(jobId, targetCandidateId);
        if (refreshResponse.success) {
          setJobApplications(refreshResponse.data || []);
        }
      } else {
        setError(response.message || "Failed to add job comment");
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('Error adding job comment:', err);
      setError("Network error while adding comment");
      setShowErrorPopup(true);
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!editFormData.status) {
      setError("Status is required");
      setShowErrorPopup(true);
      return;
    }

    try {
      setSubmitting(true);
      // Create new entry instead of updating existing
      const response = await updateJobApplicationStatus(
        editingApplication.candidate_id,
        editingApplication.job_id,
        editFormData.status,
        editFormData.job_comment
      );

      if (response.success) {
        setSuccessMessage("Application status updated successfully!");
        setShowSuccessPopup(true);
        setShowEditPopup(false);
        setEditingApplication(null);
        setEditFormData({ status: "", job_comment: "" });

        // Refresh the applications list
        const refreshResponse = await getJobApplications(jobId, targetCandidateId);
        if (refreshResponse.success) {
          setJobApplications(refreshResponse.data || []);
        }
      } else {
        setError(response.message || "Failed to update application status");
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error("Error updating application:", err);
      setError("Network error while updating application");
      setShowErrorPopup(true);
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = [
    'Resume Review',
    'Resume Rejected',
    'HR Screening',
    'HR Screening Rejected',
    'Ready to Pitch',
    'Applied for Job',
    'Interview Scheduled',
    'Interview Round 1',
    'Interview Round 2',
    'Interview Round 3',
    'Interview Round 4',
    'Interview Rejected',
    'HR Interview',
    'Offered',
    'Offer Rejected',
    'Offer Accepted',
    'Onboarding',
    'Onboarded'
  ];

  if (loading) {
    return (
      <div className="scrapper-container">
        <div className="scrapper-loading-container">
          <span className="scrapper-spinner-loader"></span>
          Loading job details...
        </div>
      </div>
    );
  }

  // Determine which applications to show based on route
  let applicationsToShow = jobApplications;
  if (location.pathname === '/applied_candidate') {
    // Only show the latest row per candidate
    const latestApplicationsMap = {};
    jobApplications.forEach(app => {
      const candidateId = app.candidate_id;
      if (
        !latestApplicationsMap[candidateId] ||
        new Date(app.created_at || 0) > new Date(latestApplicationsMap[candidateId].created_at || 0)
      ) {
        latestApplicationsMap[candidateId] = app;
      }
    });
    applicationsToShow = Object.values(latestApplicationsMap);
  }

  return (
    <div className="scrapper-container">
      <div className="scrapper-header">
        {/* ...existing code... */}
      </div>

      {/* Job Info Card */}
      {job && (
        <div className="feedback-candidate-card">
          <div className="feedback-candidate-info">
            <div className="feedback-candidate-header">
              <div>
                <h3 className="feedback-candidate-name">{job.job_title || job.title}</h3>
                <p className="feedback-candidate-summary"><strong>Company:</strong> {job.job_company || job.company}</p>
                <p className="feedback-candidate-summary"><strong>Location:</strong> {job.job_location || job.location}</p>
                <p className="feedback-candidate-summary"><strong>Type:</strong> {job.job_type}</p>
              </div>
              <button
                type="button"
                className="toggle-details-btn"
                onClick={() => setShowJobDetails(prev => !prev)}
              >
                {showJobDetails ? 'Hide Job Details' : 'Show Job Details'}
              </button>
            </div>
            {showJobDetails && (
              <div className="feedback-candidate-details">
                <p><strong>Job ID:</strong> {job.job_id}</p>
                <p><strong>Company:</strong> {job.job_company || job.company}</p>
                <p><strong>Location:</strong> {job.job_location || job.location}</p>
                <p><strong>Type:</strong> {job.job_type}</p>
                <p><strong>Remote Work:</strong> {job.remote_work ? 'Yes' : 'No'}</p>
                <p><strong>Budget:</strong> {job.budget ? `₹${parseInt(job.budget).toLocaleString()}` : 'N/A'}</p>
                <p><strong>Experience Required:</strong> {job.experience_required} years</p>
                <p><strong>Department:</strong> {job.department}</p>
                <p><strong>Description:</strong> {job.job_description || job.description}</p>
                <p><strong>Skills:</strong> {job.skill}</p>
                <p><strong>Posted On:</strong> {job.posted_on ? new Date(job.posted_on).toLocaleDateString() : 'N/A'}</p>
                <p><strong>Closing On:</strong> {job.closing_on ? new Date(job.closing_on).toLocaleDateString() : 'N/A'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {job && (
        <div className="feedback-candidate-card">
          <div className="feedback-candidate-info">
            <div className="feedback-candidate-header">
              <div>
                <h3 className="feedback-candidate-name">
                  {applicationSources.length > 0 ? applicationSources[0].SourceName || 'Source Details' : 'Source Details'}
                </h3>
                <p className="feedback-candidate-summary"><strong>Company:</strong> {applicationSources[0]?.CompanyName || 'N/A'}</p>
                <p className="feedback-candidate-summary"><strong>Location:</strong> {applicationSources[0]?.City || applicationSources[0]?.State || applicationSources[0]?.Country || 'N/A'}</p>
                <p className="feedback-candidate-summary"><strong>Type:</strong> {applicationSources[0]?.SourceType || 'N/A'}</p>
              </div>
              <button
                type="button"
                className="toggle-details-btn"
                onClick={() => setShowSourceDetails(prev => !prev)}
              >
                {showSourceDetails ? 'Hide Source Details' : 'Show Source Details'}
              </button>
            </div>
            {showSourceDetails && (
              <div className="feedback-candidate-details">
                {applicationSources.length > 0 ? (
                  applicationSources.map((source, index) => (
                    <React.Fragment key={source.id || index}>
                      <p><strong>Source Name:</strong> {source.SourceName || 'N/A'}</p>
                      <p><strong>Source Type:</strong> {source.SourceType || 'N/A'}</p>
                      <p><strong>Job Link:</strong> {source.JobLink ? (
                        <a href={source.JobLink} target="_blank" rel="noopener noreferrer" style={{ color: '#1a73e8' }}>
                          Open Link
                        </a>
                      ) : 'N/A'}</p>
                      <p><strong>Contact Person:</strong> {source.ContactPersonName || 'N/A'}</p>
                      <p><strong>Contact Mobile:</strong> {source.ContactPersonMobile || 'N/A'}</p>
                      <p><strong>Contact Email:</strong> {source.ContactPersonEmail || 'N/A'}</p>
                      <p><strong>Contact LinkedIn:</strong> {source.ContactPersonLinkedIn || 'N/A'}</p>
                      <p><strong>Secondary Contact Name:</strong> {source.SecondaryContactName || 'N/A'}</p>
                      <p><strong>Secondary Contact Mobile:</strong> {source.SecondaryContactMobile || 'N/A'}</p>
                      <p><strong>Secondary Contact Email:</strong> {source.SecondaryContactEmail || 'N/A'}</p>
                      <p><strong>WhatsApp Group:</strong> {source.WhatsAppGroup || 'N/A'}</p>
                      <p><strong>Company:</strong> {source.CompanyName || 'N/A'}</p>
                      <p><strong>Company Website:</strong> {source.CompanyWebsite ? (
                        <a href={source.CompanyWebsite} target="_blank" rel="noopener noreferrer" style={{ color: '#1a73e8' }}>
                          Open Website
                        </a>
                      ) : 'N/A'}</p>
                      <p><strong>Company LinkedIn:</strong> {source.CompanyLinkedIn ? (
                        <a href={source.CompanyLinkedIn} target="_blank" rel="noopener noreferrer" style={{ color: '#1a73e8' }}>
                          Open LinkedIn
                        </a>
                      ) : 'N/A'}</p>
                      <p><strong>Country:</strong> {source.Country || 'N/A'}</p>
                      <p><strong>State:</strong> {source.State || 'N/A'}</p>
                      <p><strong>City:</strong> {source.City || 'N/A'}</p>
                      <p><strong>Source Status:</strong> {source.SourceStatus || 'N/A'}</p>
                      <p><strong>Notes:</strong> {source.Notes || 'N/A'}</p>
                      <p><strong>Last Contact Date:</strong> {source.LastContactDate ? new Date(source.LastContactDate).toLocaleDateString('en-IN') : 'N/A'}</p>
                      <p><strong>Next Follow Up Date:</strong> {source.NextFollowUpDate ? new Date(source.NextFollowUpDate).toLocaleDateString('en-IN') : 'N/A'}</p>
                    </React.Fragment>
                  ))
                ) : (
                  <p>No source details available.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Applications Table */}
      <div className="scrapper-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Application History</h3>
          <div style={{ display: 'flex', gap: '12px' }}>
            {showAddCommentButton && (
              <button
                className="scrapper-link-button"
                type="button"
                onClick={handleOpenAddComment}
                disabled={jobApplications.length === 0}
              >
                ➕ Add Job Comment
              </button>
            )}
            {isAppliedCandidateRoute && (
              <button
                className="scrapper-link-button"
                type="button"
                onClick={() => {
                  setShowAddCandidatePopup(true);
                  setCandidateSearch("");
                  setSelectedCandidateId(null);
                }}
              >
                ➕ Add Candidate
              </button>
            )}
          </div>
        </div>
        <table className="scrapper-table">
          <thead>
            <tr>
              <th className="scrapper-th">SrNo</th>
              <th className="scrapper-th">Candidate Name</th>
              <th className="scrapper-th">Status</th>
              <th className="scrapper-th">Job Comment</th>
              <th className="scrapper-th">Created At</th>
              <th className="scrapper-th">Created By</th>
              <th className="scrapper-th">Modified At</th>
            </tr>
          </thead>
          <tbody>
            {applicationsToShow.length > 0 ? (
              applicationsToShow.map((application, idx) => (
                <tr key={application.mapping_id} className="scrapper-tr-hover">
                  <td className="scrapper-td">{idx + 1}</td>
                  <td className="scrapper-td">
                    <button
                      type="button"
                      className="scrapper-link-button"
                      style={{ padding: 0, background: 'none', color: '#1a73e8', textDecoration: 'underline', cursor: 'pointer', border: 'none' }}
                      onClick={() => {
                        if (location.pathname === '/applied_candidate') {
                          setCandidateId(application.candidate_id);
                          navigate('/job-detail', { state: { job: job, candidateId: application.candidate_id } });
                        } else if (location.pathname === '/job-detail') {
                          navigate(`/feedback/${application.candidate_id}`);
                        }
                      }}
                    >
                      {application.candidate_name || application.candidate_id}
                    </button>
                  </td>
                  <td className="scrapper-td">
                    <span className={`scrapper-status-badge scrapper-status-${String(application.status || 'applied').toLowerCase()}`}>
                      {application.status ? application.status.charAt(0).toUpperCase() + application.status.slice(1) : 'Applied'}
                    </span>
                  </td>
                  <td className="scrapper-td scrapper-cell-wrap">{application.job_comment || 'No comments'}</td>
                  <td className="scrapper-td">
                    {application.created_at ? new Date(application.created_at).toLocaleDateString('en-IN') : 'N/A'}
                  </td>
                  <td className="scrapper-td">{application.created_by || 'N/A'}</td>
                  <td className="scrapper-td">
                    {application.modified_at ? new Date(application.modified_at).toLocaleDateString('en-IN') : 'N/A'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="scrapper-no-data">
                  No applications found for this job.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Status Popup */}
      {showEditPopup && editingApplication && (
        <div className="popup-overlay" onClick={() => setShowEditPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Update Application Status</h3>
              <button
                className="popup-close"
                onClick={() => setShowEditPopup(false)}
              >
                ✕
              </button>
            </div>
            <div className="popup-body">
              <div className="popup-message">
                <div className="popup-title">{job?.title || job?.job_title || 'Job Details'}</div>
                <div className="popup-text" style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                  Update the application status and add comments.
                </div>
              </div>

              <form onSubmit={handleEditSubmit}>
                <div className="form-group" style={{ marginTop: '20px' }}>
                  <label className="form-label">Status:</label>
                  <select
                    name="status"
                    value={editFormData.status}
                    onChange={handleEditFormChange}
                    className="form-input"
                    required
                  >
                    <option value="">-- Select Status --</option>
                    {statusOptions.map(status => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Comments:</label>
                  <textarea
                    name="job_comment"
                    value={editFormData.job_comment}
                    onChange={handleEditFormChange}
                    className="form-textarea"
                    rows="4"
                    placeholder="Add any comments..."
                  />
                </div>

                <div className="popup-footer">
                  <button
                    type="submit"
                    className="popup-btn popup-btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Updating...' : 'Update Status'}
                  </button>
                  <button
                    type="button"
                    className="popup-btn popup-btn-secondary"
                    onClick={() => setShowEditPopup(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showAddCommentPopup && (
        <div className="popup-overlay" onClick={() => setShowAddCommentPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Add Job Comment</h3>
              <button
                className="popup-close"
                onClick={() => setShowAddCommentPopup(false)}
              >
                ✕
              </button>
            </div>
            <div className="popup-body">
              
                  <div className="popup-message popup-title">Add comment for candidate</div>
                

              <form onSubmit={handleAddCommentSubmit}>
                <div className="form-group" style={{ marginTop: '20px' }}>
                  <label className="form-label">Status:</label>
                  <select
                    name="status"
                    value={newCommentStatus}
                    onChange={handleCommentStatusChange}
                    className="form-input"
                    required
                    style={{ width: '100%' }}
                  >
                    <option value="">-- Select Status --</option>
                    {resourceStatusOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginTop: '20px' }}>
                  <label className="form-label">Comment:</label>
                  <textarea
                    name="job_comment"
                    value={newComment}
                    onChange={handleAddCommentChange}
                    className="form-textarea"
                    rows="5"
                    placeholder="Enter your job comment..."
                  />
                </div>

                <div className="popup-footer">
                  <button
                    type="submit"
                    className="popup-btn popup-btn-primary"
                    disabled={commentSubmitting}
                  >
                    {commentSubmitting ? 'Saving...' : 'Save Comment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showAddCandidatePopup && (
        <div className="popup-overlay" onClick={closeAddCandidatePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Apply Job</h3>
              <button className="popup-close" onClick={closeAddCandidatePopup}>✕</button>
            </div>
            <div className="popup-body">
              <label htmlFor="candidateSearch" className="popup-label">Search by Candidate ID or Name</label>
              <input
                id="candidateSearch"
                type="text"
                value={candidateSearch}
                onChange={(e) => setCandidateSearch(e.target.value)}
                placeholder="Type Candidate ID or Name..."
                className="popup-input"
                disabled={candidatesLoading || applyingCandidate}
              />
              <div className="popup-job-list">
                {candidatesLoading ? (
                  <p className="popup-loading">Loading candidates...</p>
                ) : filteredCandidates.length === 0 ? (
                  <p className="popup-no-results">No matching candidates found.</p>
                ) : (
                  <table className="popup-job-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Primary Skill</th>
                        <th>Experience</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCandidates.map(candidate => (
                        <tr
                          key={candidate.CandidateID}
                          className={selectedCandidateId === candidate.CandidateID ? 'popup-job-row selected' : 'popup-job-row'}
                          onClick={() => setSelectedCandidateId(candidate.CandidateID)}
                        >
                          <td className="popup-job-radio-cell">
                            <input
                              type="radio"
                              name="selectedCandidate"
                              checked={selectedCandidateId === candidate.CandidateID}
                              onChange={() => setSelectedCandidateId(candidate.CandidateID)}
                            />
                          </td>
                          <td>{candidate.FullName || 'N/A'}</td>
                          <td>{candidate.Role || 'N/A'}</td>
                          <td>{candidate.PrimarySkills || 'N/A'}</td>
                          <td>{candidate.YearsOfExperience || 'N/A'}</td>
                          <td>{candidate.CurrentLocation || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="popup-footer">
              <button
                className="popup-btn popup-btn-primary"
                onClick={handleAddCandidateSubmit}
                disabled={applyingCandidate || !selectedCandidateId}
              >
                {applyingCandidate ? '⏳ Applying...' : 'Apply Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      <PopupMessage
        show={showErrorPopup}
        message={error}
        type="error"
        onClose={() => setShowErrorPopup(false)}
      />

      {/* Success Popup */}
      <PopupMessage
        show={showSuccessPopup}
        message={successMessage}
        type="success"
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
  );
};

export default JobDetailPage;